<template>
    <vx-expandable-card id="details">
        <template v-slot:header>Details</template>
        <template v-slot:content>
            <div class="flex flex-col gap-6">
                <div data-test="phone-wrapper">
                    <div>
                        <vx-badge v-if="!contact.id" color="rose" size="xs">
                            Required
                            <font-awesome-icon widthAuto class="text-rose-500" :icon="faAsterisk"></font-awesome-icon>
                        </vx-badge>
                    </div>
                    <vx-label> Phone </vx-label>
                    <vx-input
                        size="lg"
                        v-model="contact.phone"
                        :readonly="contact.id"
                        @blur="e164FormatPhoneNumber()"
                        :error="displayFirstError(validate.phone)"
                    ></vx-input>
                </div>

                <div data-test="first-name-wrapper">
                    <vx-label> First Name </vx-label>
                    <vx-input
                        size="lg"
                        v-model="contact.first_name"
                        :error="displayFirstError(validate.first_name)"
                    ></vx-input>
                </div>

                <div data-test="last-name-wrapper">
                    <vx-label> Last Name </vx-label>
                    <vx-input
                        size="lg"
                        v-model="contact.last_name"
                        :error="displayFirstError(validate.last_name)"
                    ></vx-input>
                </div>

                <div v-if="!FEATURES.local_attributes" data-test="email-wrapper">
                    <vx-label> Email </vx-label>
                    <vx-input size="lg" v-model="contact.email" :error="displayFirstError(validate.email)"></vx-input>
                </div>

                <div data-test="time-zone-wrapper">
                    <vx-label> Time Zone </vx-label>
                    <vx-selectable
                        size="lg"
                        v-model="contact.time_zone"
                        :options="timeZoneOptions(contact.time_zone)"
                        :error="displayFirstError(validate.time_zone)"
                    ></vx-selectable>
                </div>

                <div v-if="!FEATURES.local_attributes" data-test="entry-channel-wrapper">
                    <vx-label> Entry Channel </vx-label>
                    <vx-input
                        size="lg"
                        v-model="contact.entry_channel"
                        :error="displayFirstError(validate.entry_channel)"
                    ></vx-input>
                </div>

                <div v-if="!FEATURES.local_attributes" data-test="membership-status-wrapper">
                    <vx-label> Membership Status </vx-label>
                    <vx-input
                        size="lg"
                        v-model="contact.membership_status"
                        :error="displayFirstError(validate.membership_status)"
                    ></vx-input>
                </div>

                <div v-if="!FEATURES.local_attributes" data-test="lifecycle-status-wrapper">
                    <vx-label> Lifecycle Status </vx-label>
                    <vx-input
                        size="lg"
                        v-model="contact.lifecycle_status"
                        :error="displayFirstError(validate.lifecycle_status)"
                    ></vx-input>
                </div>

                <div v-if="contact.id" :class="gridClasses">
                    <div :class="gridKeyClasses">Created:</div>
                    <div :class="gridValueClasses">
                        {{ contact.created_at ? shortDateTime(contact.created_at) : '-' }}
                    </div>
                    <div v-if="groupId" :class="gridKeyClasses">Added:</div>
                    <div v-if="groupId" :class="gridValueClasses">
                        {{ contact.added_at ? shortDateTime(contact.added_at) : '-' }}
                    </div>
                    <div :class="gridKeyClasses">Entry Point:</div>
                    <div :class="gridValueClasses">
                        {{ entryPoint(contact.entry_point) }}
                    </div>
                    <div v-if="!groupId" :class="gridKeyClasses">Sticky Phone Number:</div>
                    <div v-if="!groupId" :class="gridValueClasses">
                        <vx-icon-loading v-if="loadingStickyPhoneNumber" spin></vx-icon-loading>
                        <div v-else>
                            {{ stickyPhoneNumber ? formatPhoneNumber(stickyPhoneNumber.phone_number) : '-' }}
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { faAsterisk } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxExpandableCard, VxIconLoading, VxSelectable, VxLabel, VxInput } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { email, maxLength, required } from '@vuelidate/validators';
import { entryPoint, formatPhoneNumber } from '~/components/filters';
import { shortDateTime } from '~/utils/date';
import { timeZoneOptions } from '~/utils/timeZones';
import { csvInjection, displayFirstError, phoneNumber, customAttributeValue } from '~/utils/validation';
import { inject } from 'vue';
import { useContactDetails } from '../useContactDetails';
import { e164Format } from '~/utils/phoneNumber';

const gridClasses = 'grid grid-cols-12 items-center gap-y-2 gap-x-1 text-slate-800 text-sm mb-6';
const gridKeyClasses = 'col-span-3 break-all text-xs text-slate-500';
const gridValueClasses = 'col-span-9 break-all';

const FEATURES = inject('FEATURES');

const props = defineProps({
    contactId: {
        type: Number,
        required: false,
    },
});

const {
    groupId,

    contact,
    loadingStickyPhoneNumber,
    stickyPhoneNumber,
} = useContactDetails();

const validate = useVuelidate(
    {
        phone: !props.contactId
            ? {
                  required: required,
                  phoneNumber: phoneNumber,
              }
            : {},
        first_name: {
            csvInjection: csvInjection,
            maxLength: maxLength(80),
        },
        last_name: {
            csvInjection: csvInjection,
            maxLength: maxLength(80),
        },
        email: !FEATURES.local_attributes
            ? {
                  csvInjection: csvInjection,
                  maxLength: maxLength(191),
                  email: email,
              }
            : {},
        time_zone: {
            maxLength: maxLength(30),
        },
        entry_channel: !FEATURES.local_attributes
            ? {
                  csvInjection: csvInjection,
                  maxLength: maxLength(40),
                  format: customAttributeValue,
              }
            : {},
        membership_status: !FEATURES.local_attributes
            ? {
                  csvInjection: csvInjection,
                  maxLength: maxLength(40),
                  format: customAttributeValue,
              }
            : {},
        lifecycle_status: !FEATURES.local_attributes
            ? {
                  csvInjection: csvInjection,
                  maxLength: maxLength(40),
                  format: customAttributeValue,
              }
            : {},
    },
    contact
);

const e164FormatPhoneNumber = () => {
    const phone = e164Format(contact.value.phone);

    if (phone) {
        contact.value.phone = phone;
    }
};
</script>
